import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";
import { TextField, AreaField, SelectField, ConsentBox, Honeypot, Eyebrow } from "@/components/form";
import { usePageMeta } from "@/hooks/usePageMeta";
import { api, apiError } from "@/lib/api";

const TIMELINES = ["Not sure yet", "Within 3 months", "3–6 months", "6–12 months", "More than a year"];
const RELATIONSHIPS = ["Pastor / Church leader", "Board or elder", "Denomination representative", "Property owner", "Realtor / Broker", "Other"];

const EMPTY = {
  contact_name: "", organization: "", email: "", phone: "", city: "", state: "",
  relationship: "", description: "", timeline: "", consent: false, hp_field: "",
};

export default function Churches() {
  usePageMeta("Churches & Properties",
    "For church leaders facing closure or considering a property transition — start a compassionate conversation with Lord Almighty.");
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please provide consent to be contacted.");
    setSubmitting(true);
    try {
      await api.post("/inquiries/church", form);
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHero
        eyebrow="Churches & Properties"
        title="When a season ends, the mission can continue"
        subtitle="If your congregation is facing closure or considering a property transition, we welcome a compassionate, unhurried conversation about acquiring closing church properties and exploring continued ministry use."
        image="https://images.unsplash.com/photo-1438032005730-c779502df39b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxjaHVyY2glMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3OTA2MDYzNzJ8MA&ixlib=rb-4.1.0&q=85"
      />

      <section className="mx-auto max-w-3xl px-5 lg:px-8 py-16">
        <Reveal>
          <div className="rounded-xl border border-border bg-white p-7 shadow-sm">
            <HeartHandshake className="h-7 w-7 text-emerald" />
            <p className="mt-4 text-muted-foreground leading-relaxed">
              We understand these decisions carry deep history and emotion. Reaching out does not commit
              you to anything. A conversation is simply a conversation — we cannot promise a purchase,
              funding, or a specific outcome, but we are glad to listen and explore possibilities
              together. Please do not send sensitive legal or financial documents through this form.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-3xl px-5 lg:px-8 pb-24">
        {done ? (
          <Reveal>
            <div className="rounded-xl border border-emerald/30 bg-emerald/5 p-10 text-center" data-testid="church-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald" />
              <h2 className="mt-4 font-display text-3xl text-navy font-semibold">Thank you for reaching out</h2>
              <p className="mt-3 text-muted-foreground">
                We've received your message and will be in touch. May the Lord give you peace and wisdom
                in this season.
              </p>
              <Button className="mt-6 rounded-full bg-emerald hover:bg-emerald-dark text-white px-8"
                onClick={() => { setForm(EMPTY); setDone(false); }}>Submit another</Button>
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <Eyebrow>Start a conversation</Eyebrow>
            <h2 className="mt-3 font-display text-4xl font-semibold text-navy tracking-tight">Property inquiry</h2>
            <form onSubmit={submit} className="mt-8 grid gap-5" data-testid="church-form">
              <Honeypot value={form.hp_field} onChange={set("hp_field")} />
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField label="Contact name" name="contact_name" required value={form.contact_name} onChange={onInput("contact_name")} />
                <TextField label="Church or organization name" name="organization" required value={form.organization} onChange={onInput("organization")} />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField label="Email" name="email" type="email" required value={form.email} onChange={onInput("email")} />
                <TextField label="Phone (optional)" name="phone" value={form.phone} onChange={onInput("phone")} />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField label="Property city" name="city" required value={form.city} onChange={onInput("city")} />
                <TextField label="State" name="state" required value={form.state} onChange={onInput("state")} />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <SelectField label="Relationship to the property" name="relationship" required
                  value={form.relationship} onValueChange={set("relationship")} placeholder="Select one" options={RELATIONSHIPS} />
                <SelectField label="Approximate timeline" name="timeline"
                  value={form.timeline} onValueChange={set("timeline")} placeholder="Select one" options={TIMELINES} />
              </div>
              <AreaField label="Brief description" name="description" required rows={5} value={form.description} onChange={onInput("description")}
                hint="Tell us a little about the property and your situation. No sensitive documents, please." />
              <ConsentBox checked={form.consent} onChange={set("consent")}>
                I consent to be contacted by Lord Almighty regarding this inquiry.
              </ConsentBox>
              <div>
                <Button type="submit" disabled={submitting}
                  className="rounded-full bg-emerald hover:bg-emerald-dark text-white px-10 py-6 text-base" data-testid="church-submit">
                  {submitting ? "Sending…" : "Start a Conversation"}
                </Button>
              </div>
            </form>
          </Reveal>
        )}
      </section>
    </div>
  );
}
